import { IComboBoxOption, MessageBar, MessageBarType, Stack } from '@fluentui/react';
import { PageHeader, useAppNavigationContext, useHaicPageTitle, useTheme } from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryAuditTrail } from '../../audit-trail/hooks';
import { AuditTrailUserActionType, resourceName } from '../../audit-trail/userActions';
import { defaultAppstoreWorkspaceName } from '../../authz/authz.hooks';
import { usePermissions } from '../../authz/providers/PermissionsProvider/PermissionsProvider';
import { FilterState } from '../../components/PageFilters/types';
import { stackStylesNoLeftNav } from '../../themes/themes';
import { LocalStorageKeys, useEnv, useLocalStorage } from '../../utils/hooks';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { AuditTrailList } from './components/AuditTrailList';
import { AuditTrailPageControls } from './components/AuditTrailPageControls/AuditTrailPageControls';
import { defaultColumns } from './constants';
import { useAuditTrailFilterSync } from './hooks/useAuditTrailFilterSync';
import { useAuditTrailQueryMapping } from './hooks/useAuditTrailQueryMapping';

export const AuditTrailPage = () => {
  const theme = useTheme();
  const { ACTIVE_WORKSPACE_NAME = defaultAppstoreWorkspaceName } = useWorkspaces();
  const { enableWorkspacesDropdown, disableWorkspacesDropdown } = useAppNavigationContext();
  const env = useEnv();

  const [canSearchAsAdmin, adminPermissionLoading] = usePermissions(
    [AuditTrailUserActionType.AUDIT_TRAIL_SEARCH],
    resourceName
  );
  const [canSearchInWorkspace, searchInWorkspacePermissionLoading] = usePermissions(
    [AuditTrailUserActionType.AUDIT_TRAIL_SEARCH],
    ACTIVE_WORKSPACE_NAME
  );

  const canSearch = canSearchAsAdmin || canSearchInWorkspace;
  const permissionLoading = adminPermissionLoading || searchInWorkspacePermissionLoading;

  const [pageColumns, setPageColumns] = useState<string[]>([]);
  const [selectedPageColumns, setSelectedPageColumns] = useLocalStorage<string[]>(
    LocalStorageKeys.AUDIT_TRAIL_COLUMNS,
    defaultColumns
  );

  const {
    filters,
    setFilters,
    clearFilters,
    showReadOnlyEvents,
    setShowReadOnlyEvents,
    allWorkspaces,
    setAllWorkspaces,
  } = useAuditTrailFilterSync();
  const handleToggleWorkspaces = useCallback(() => {
    const newAllWorkspaces = !allWorkspaces;
    setAllWorkspaces(newAllWorkspaces);

    // Clear Workspaces filter when enabling "Search within all Workspaces"
    if (newAllWorkspaces && filters['audit-trail-workspace-filter']) {
      const newFilters = { ...filters };
      delete newFilters['audit-trail-workspace-filter'];
      setFilters(newFilters);
    }
  }, [allWorkspaces, setAllWorkspaces, filters, setFilters]);
  const handleToggleReadOnlyEvents = useCallback(
    () => setShowReadOnlyEvents(!showReadOnlyEvents),
    [showReadOnlyEvents, setShowReadOnlyEvents]
  );

  const queryRequest = useAuditTrailQueryMapping({
    filters,
    allWorkspaces,
    ACTIVE_WORKSPACE_NAME,
    showReadOnlyEvents,
  });

  const {
    data: auditTrailData,
    isLoading: eventsLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useQueryAuditTrail(queryRequest, {
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
  });

  const originalEvents = useMemo(() => {
    if (!auditTrailData?.pages) return [];
    return auditTrailData.pages.flatMap((page) => page.events || []);
  }, [auditTrailData]);

  // Create flattened events for display
  const auditTrailEvents = useMemo(() => {
    return originalEvents.map((event) => {
      const flattenedEvent: any = {};

      Object.entries(event).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // Flatten nested objects
          const objectKeys = Object.keys(value);
          if (objectKeys.length === 0) {
            // Empty object
            flattenedEvent[key] = '{}';
          } else {
            // Flatten with dot notation
            Object.entries(value).forEach(([nestedKey, nestedValue]) => {
              flattenedEvent[`${key}.${nestedKey}`] = nestedValue;
            });
          }
        } else {
          // Keep primitive values and arrays as-is
          flattenedEvent[key] = value;
        }
      });

      return flattenedEvent;
    });
  }, [originalEvents]);

  const searchedUntilTime = useMemo(() => {
    if (!auditTrailData?.pages || auditTrailData.pages.length === 0) return null;
    const lastPage = auditTrailData.pages[auditTrailData.pages.length - 1];
    return lastPage.searchedUntilTime;
  }, [auditTrailData]);

  const availableColumns: IComboBoxOption[] = useMemo(() => {
    return pageColumns.map((column) => ({ key: column, text: column, disabled: defaultColumns.includes(column) }));
  }, [pageColumns]);

  const isLoading = permissionLoading || eventsLoading || isFetching;

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleFiltersChange = useCallback(
    (newFilters: Record<string, FilterState<any>>) => {
      setFilters(newFilters);
    },
    [setFilters]
  );

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleDownloadJson = useCallback(() => {
    if (originalEvents.length > 0) {
      const jsonString = JSON.stringify(originalEvents, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-trail-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }, [originalEvents]);

  useEffect(() => {
    if (auditTrailEvents[0]) setPageColumns(Object.keys(auditTrailEvents[0]));
  }, [auditTrailEvents]);

  useEffect(() => {
    if (allWorkspaces) disableWorkspacesDropdown();
    else enableWorkspacesDropdown();
  }, [allWorkspaces]);

  useHaicPageTitle('Audit Trail', env?.cloudInstanceName);

  return (
    <Stack styles={stackStylesNoLeftNav}>
      <PageHeader pageTitle="Audit Trail" />

      <Stack
        styles={{ root: { position: 'sticky', paddingTop: 8, top: 0, background: theme.palette?.gray200, zIndex: 1 } }}
      >
        <AuditTrailPageControls
          columns={availableColumns}
          selectedColumns={selectedPageColumns || []}
          onColumnsChange={setSelectedPageColumns}
          onFiltersChange={handleFiltersChange}
          onClearFilters={clearFilters}
          currentFilters={filters}
          allWorkspaces={allWorkspaces}
          toggleAllWorkspacesDisabled={adminPermissionLoading || !canSearchAsAdmin}
          toggleAllWorkspaces={handleToggleWorkspaces}
          showReadOnlyEvents={showReadOnlyEvents}
          toggleShowReadOnlyEvents={handleToggleReadOnlyEvents}
          onRefresh={handleRefresh}
          isLoading={isLoading}
          onDownloadJson={handleDownloadJson}
          hasContent={originalEvents.length > 0}
        />
      </Stack>

      {!isLoading && !canSearch ? (
        <MessageBar styles={{ root: { marginTop: 16 } }} messageBarType={MessageBarType.error}>
          You are not authorized to access Audit Trail{!canSearchInWorkspace && ' in this workspace'}.
        </MessageBar>
      ) : error ? (
        <MessageBar styles={{ root: { marginTop: 16 } }} messageBarType={MessageBarType.error}>
          Failed to load audit trail data. Please try refreshing the page. <br />
          {!(error as any)?.message ? '' : <i>{(error as any)?.message}</i>}
        </MessageBar>
      ) : (
        <AuditTrailList
          loading={isLoading}
          events={auditTrailEvents}
          columns={selectedPageColumns || []}
          hasMorePages={!!hasNextPage}
          onLoadMore={handleLoadMore}
          isLoadingMore={isFetchingNextPage}
          searchedUntilTime={searchedUntilTime}
        />
      )}
    </Stack>
  );
};
